/* ============================================================
   VD Real Results — "Proof, at the follicle" carousel
   • Native scroll-snap track; dots (desktop) and prev/next (mobile)
     are built from how many cards fit per page.
   • Endless loop (same as the "See the difference" carousel): the card nearest an
     end is moved to the other end, so arrows, dots and swipes never hit a dead end.
   • Before/after compare: a transparent range input drives --vd-rrp-pos.
   Vanilla JS, no deps, idempotent, theme-editor aware.
   ============================================================ */
(function () {
  'use strict';

  if (window.__vdRrpLoaded) return;
  window.__vdRrpLoaded = true;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initCompare(root) {
    root.querySelectorAll('[data-vd-rrp-compare]').forEach(function (frame) {
      var range = frame.querySelector('[data-vd-rrp-range]');
      if (!range || range.dataset.vdRrpInit === '1') return;
      range.dataset.vdRrpInit = '1';

      function sync() { frame.style.setProperty('--vd-rrp-pos', range.value); }
      range.addEventListener('input', sync);
      sync();
    });
  }

  function initCarousel(root) {
    root.querySelectorAll('[data-vd-rrp]').forEach(function (section) {
      if (section.dataset.vdRrpInit === '1') return;
      section.dataset.vdRrpInit = '1';

      var scroller = section.querySelector('[data-vd-rrp-scroller]');
      var cards = Array.prototype.slice.call(section.querySelectorAll('[data-vd-rrp-card]'));
      var dotsWrap = section.querySelector('[data-vd-rrp-dots]');
      var prev = section.querySelector('[data-vd-rrp-prev]');
      var next = section.querySelector('[data-vd-rrp-next]');
      if (!scroller || !cards.length) return;

      var count = cards.length;
      var perPage = 1;
      var pages = 1;
      var padLeft = 0;
      var looping = false;
      var buffer = 2;
      var touching = false;
      var resizing = false;
      var settleTimer;

      /* Cards get moved around, so each keeps its place in the editor order. */
      cards.forEach(function (card, i) { card.dataset.vdRrpIndex = String(i); });

      function measure() {
        var cs = getComputedStyle(scroller);
        var gap = parseFloat(cs.columnGap) || 0;
        var cardWidth = cards[0].getBoundingClientRect().width;
        padLeft = parseFloat(cs.paddingLeft) || 0;
        var visible = scroller.clientWidth - padLeft - (parseFloat(cs.paddingRight) || 0);
        /* +2px tolerance: the desktop track is sized to fit two cards exactly. */
        perPage = Math.max(1, Math.floor((visible + gap + 2) / (cardWidth + gap)));
        pages = Math.ceil(count / perPage);
        /* Endless loop: needs a page of cards to spare on both sides of the one on screen. */
        buffer = perPage + 1;
        looping = count >= perPage + 2 * buffer;
      }

      function maxScroll() { return scroller.scrollWidth - scroller.clientWidth; }

      function cardLeft(card) {
        /* The scroller is the cards' offset parent, so offsetLeft is scroller-relative. */
        return Math.min(card.offsetLeft - padLeft, maxScroll());
      }

      /* Position in the current DOM order of the card nearest the viewport start. */
      function nearest() {
        var x = scroller.scrollLeft;
        var best = 0;
        for (var i = 1; i < count; i++) {
          if (Math.abs(cardLeft(cards[i]) - x) < Math.abs(cardLeft(cards[best]) - x)) best = i;
        }
        return best;
      }

      function logical(card) { return Number(card.dataset.vdRrpIndex); }

      /* Move a card to the other end without changing what is on screen. Snapping is
         switched off meanwhile: browsers re-snap after a layout change on their own
         schedule, which would otherwise be added on top of the shift below. */
      function rotate(toFront) {
        var anchor = cards[nearest()];
        var offset = scroller.scrollLeft - anchor.offsetLeft;
        scroller.style.scrollSnapType = 'none';
        if (toFront) {
          var last = cards.pop();
          cards.unshift(last);
          scroller.insertBefore(last, scroller.firstChild);
        } else {
          var first = cards.shift();
          cards.push(first);
          scroller.appendChild(first);
        }
        scroller.scrollLeft = anchor.offsetLeft + offset;
        void scroller.offsetWidth;
        scroller.style.scrollSnapType = '';
      }

      /* Make sure there are cards to slide to on both sides of the one on screen, moving
         only as many as needed (usually one). Nothing moves on screen. */
      function keepRoom() {
        if (!looping) return;
        var i = nearest();
        while (i < buffer) { rotate(true); i++; }
        while (count - perPage - i < buffer) { rotate(false); i--; }
      }

      /* Slide by a number of cards (negative = back). The target card is brought
         within the DOM order first, so it can always sit at the start of the track. */
      function moveBy(delta) {
        var i = nearest();
        if (delta > 0) {
          for (var over = i + delta - (count - perPage - 1); over > 0 && i > 0; over--) { rotate(false); i--; }
        } else {
          for (var under = delta + i; under < 0 && i < count - 1; under++) { rotate(true); i++; }
        }
        var target = cards[i + delta];
        if (!target) return;
        /* Let the shifted scroll position paint before the slide animation starts. */
        requestAnimationFrame(function () {
          scroller.scrollTo({ left: cardLeft(target), behavior: reduceMotion ? 'auto' : 'smooth' });
        });
      }

      function activePage() {
        if (looping) return Math.floor(logical(cards[nearest()]) / perPage);
        var x = scroller.scrollLeft;
        var best = 0;
        for (var i = 1; i < pages; i++) {
          var a = cardLeft(cards[Math.min(i * perPage, count - 1)]);
          var b = cardLeft(cards[Math.min(best * perPage, count - 1)]);
          if (Math.abs(a - x) < Math.abs(b - x)) best = i;
        }
        return best;
      }

      function stepPage(dir) {
        if (looping) { moveBy(dir * perPage); return; }
        var page = Math.min(Math.max(activePage() + dir, 0), pages - 1);
        scroller.scrollTo({ left: cardLeft(cards[Math.min(page * perPage, count - 1)]), behavior: reduceMotion ? 'auto' : 'smooth' });
      }

      /* Dots: the card that opens page n is card n * perPage in editor order; go the
         shorter way round. */
      function goToPage(page) {
        if (!looping) {
          scroller.scrollTo({ left: cardLeft(cards[Math.min(page * perPage, count - 1)]), behavior: reduceMotion ? 'auto' : 'smooth' });
          return;
        }
        var from = logical(cards[nearest()]);
        var to = Math.min(page * perPage, count - 1);
        var delta = to - from;
        if (delta > count / 2) delta -= count;
        else if (delta < -count / 2) delta += count;
        if (delta) moveBy(delta);
      }

      function buildDots() {
        if (!dotsWrap) return;
        dotsWrap.innerHTML = '';
        for (var i = 0; i < pages; i++) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'vd-rrp__dot';
          dot.setAttribute('aria-label', 'Go to results page ' + (i + 1));
          dot.addEventListener('click', goToPage.bind(null, i));
          dotsWrap.appendChild(dot);
        }
      }

      function update() {
        /* While the window resizes the browser shifts the scroll position by itself;
           the card that was showing is restored afterwards, so don't follow the shift. */
        if (resizing) return;
        var current = activePage();
        if (dotsWrap) {
          Array.prototype.forEach.call(dotsWrap.children, function (dot, i) {
            dot.classList.toggle('is-active', i === current);
            dot.setAttribute('aria-current', i === current ? 'true' : 'false');
          });
        }
        /* The arrows only disable when the carousel is too short to loop. */
        if (prev) prev.disabled = looping ? false : scroller.scrollLeft <= 1;
        if (next) next.disabled = looping ? false : scroller.scrollLeft >= maxScroll() - 1;
      }

      /* Once scrolling stops (swipe or button), make sure the card on screen has cards
         on both sides, so the next swipe or click keeps going. */
      function settle() {
        if (touching) { settleTimer = setTimeout(settle, 140); return; }
        keepRoom();
      }

      if (prev) prev.addEventListener('click', function () { stepPage(-1); });
      if (next) next.addEventListener('click', function () { stepPage(1); });

      /* A new swipe can start before the last one has settled (quick flicks): make room
         before the finger moves, so there is never a dead end. */
      scroller.addEventListener('touchstart', function () { touching = true; keepRoom(); }, { passive: true });
      scroller.addEventListener('touchend', function () { touching = false; }, { passive: true });
      scroller.addEventListener('touchcancel', function () { touching = false; }, { passive: true });

      var ticking = false;
      scroller.addEventListener('scroll', function () {
        clearTimeout(settleTimer);
        settleTimer = setTimeout(settle, 140);
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () { ticking = false; update(); });
      }, { passive: true });

      var resizeTimer;
      var shown = null;
      window.addEventListener('resize', function () {
        /* Remember the card on screen before the browser starts shifting the scroll position. */
        if (!resizing) shown = cards[nearest()];
        resizing = true;
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(function () {
          measure();
          buildDots();
          /* Card widths changed: re-snap to the card that was showing. */
          scroller.scrollTo({ left: cardLeft(shown), behavior: 'auto' });
          resizing = false;
          keepRoom();
          update();
        }, 150);
      });

      /* Theme editor: bring the selected result card into view. */
      section.addEventListener('shopify:block:select', function (e) {
        var i = cards.indexOf(e.target);
        if (i > -1) scroller.scrollTo({ left: cardLeft(cards[i]) });
      });

      measure();
      buildDots();
      update();

      /* Start on the first card with cards waiting on both sides. Done once the page
         has its layout, so the measured card positions are the real ones. */
      var primed = false;
      function prime() {
        if (primed) return;
        if (!scroller.clientWidth) { setTimeout(prime, 200); return; }
        primed = true;
        measure();
        buildDots();
        keepRoom();
        update();
      }
      if (document.readyState === 'complete') prime();
      else window.addEventListener('load', prime);
    });
  }

  function initAll(scope) {
    var root = scope || document;
    initCompare(root);
    initCarousel(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
